import Icon from '@components/v2/ui/Icon/Icon';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import { CircularProgress, InputBase, Stack } from '@mui/material';
import { Camera, ChevronLeft, Search, X } from 'lucide-react';
import { ReactNode, RefObject } from 'react';
import { useSearchField } from '../../../../hooks/useSearchField';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../../modules/translation/TranslationContext';
import BarcodeReaderDialog from '../../../orders/products/add-product/barcode-reader/BarcodeReaderDialog';
import useSearchBarBarcodeScan from './hooks/useSearchBarBarcodeScan';
import { searchBarIconButtonSx } from './searchBar.styles';

const SEARCH_BAR_HEIGHT = 36;
const LEADING_ICON_SIZE = 16;
const MAX_QUERY_LENGTH = 120;

interface SearchBarComboboxConfig {
  listboxId?: string;
  activeDescendantId?: string;
  isResultsOpen?: boolean;
}

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  onEnter?: () => void;
  onBack?: () => void;
  isLoading?: boolean;
  placeholder: LangEnum;
  inputRef?: RefObject<HTMLInputElement>;
  testId?: string;
  combobox?: SearchBarComboboxConfig;
  askRachelSlot?: ReactNode;
}

function SearchBar({
  value,
  onChange,
  onClear,
  onEnter,
  onBack,
  isLoading,
  placeholder,
  inputRef,
  testId,
  combobox,
  askRachelSlot,
}: SearchBarProps) {
  const { translate } = useTranslation();
  const { handleChange, handleKeyDown } = useSearchField({ onChange, onClear, onEnter });
  const {
    isOpen: isBarcodeScanOpen,
    openModal: openBarcodeScan,
    closeModal: closeBarcodeScan,
    onScanSuccess,
  } = useSearchBarBarcodeScan({ onDecode: onChange });

  const { listboxId, activeDescendantId, isResultsOpen } = combobox ?? {};

  const renderLeading = () => {
    if (onBack) {
      return (
        <IconButton
          icon={ChevronLeft}
          ariaLabel={translate(LangEnum.GLOBAL_BACK)}
          testId="global-search-back"
          sx={searchBarIconButtonSx}
          onClick={onBack}
        />
      );
    }
    if (isLoading) {
      return <CircularProgress size={LEADING_ICON_SIZE} sx={{ color: 'text.secondary' }} />;
    }
    return <Icon icon={Search} size="md" />;
  };

  return (
    <>
      <Stack
        direction="row"
        sx={(theme) => ({
          alignItems: 'center',
          gap: 0.5,
          height: SEARCH_BAR_HEIGHT,
          flexShrink: 0,
          px: 1,
          borderRadius: `${theme.shape.borderRadiusXl}px`,
          backgroundColor: theme.palette.action.selected,
          color: theme.palette.text.secondary,
        })}
      >
        {renderLeading()}
        <InputBase
          value={value}
          inputRef={inputRef}
          placeholder={translate(placeholder)}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          sx={(theme) => ({
            flex: 1,
            minWidth: 0,
            color: 'text.primary',
            ...theme.typography.body1,
          })}
          inputProps={{
            maxLength: MAX_QUERY_LENGTH,
            role: listboxId ? 'combobox' : undefined,
            'aria-label': translate(placeholder),
            'aria-activedescendant': activeDescendantId,
            'aria-controls': listboxId,
            'aria-expanded': listboxId ? Boolean(isResultsOpen) : undefined,
            'aria-autocomplete': listboxId ? 'list' : undefined,
            'test-id': testId,
          }}
        />
        {askRachelSlot}
        {!value && (
          <Tooltip TooltipContent={translate(LangEnum.GLOBAL_SEARCH_SCAN_BARCODE)} tabIndex={-1}>
            <IconButton
              icon={Camera}
              ariaLabel={translate(LangEnum.GLOBAL_SEARCH_SCAN_BARCODE)}
              testId="global-search-scan-barcode"
              sx={searchBarIconButtonSx}
              onClick={openBarcodeScan}
            />
          </Tooltip>
        )}
        {value && (
          <Tooltip TooltipContent={translate(LangEnum.GLOBAL_SEARCH_CLEAR)} tabIndex={-1}>
            <IconButton
              icon={X}
              ariaLabel={translate(LangEnum.GLOBAL_SEARCH_CLEAR)}
              sx={searchBarIconButtonSx}
              onClick={onClear}
            />
          </Tooltip>
        )}
      </Stack>
      <BarcodeReaderDialog
        open={isBarcodeScanOpen}
        onClose={closeBarcodeScan}
        onSuccess={onScanSuccess}
      />
    </>
  );
}

export type { SearchBarProps };
export default SearchBar;
